import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { subscriptionPlanApi } from '@/services/subscriptionApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Edit, Trash2, CreditCard, Copy, CheckCircle2, XCircle } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { SubscriptionPlan } from '@/types/subscription';

export const SubscriptionPlanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('edit', 'subscriptionPlan');
  const canDelete = hasPermission('delete', 'subscriptionPlan');
  const canActivate = hasPermission('activate', 'subscriptionPlan');

  // Use entity hook for fetching plan
  const {
    data: plan,
    isLoading,
    error,
    refetch,
  } = useEntity<SubscriptionPlan>({
    fetchFn: (planId: string) => subscriptionPlanApi.getById(planId),
    entityId: id,
    entityName: 'subscriptionPlan',
    enabled: !!id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Subscription Plan ID is required');
      await subscriptionPlanApi.delete(id);
    },
    entityName: 'subscriptionPlan',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/subscription-plans';
    },
  });

  // Status change mutation
  const statusChangeMutation = useMutation({
    mutationFn: async (action: 'activate' | 'deactivate' | 'archive') => {
      if (!id) throw new Error('Subscription Plan ID is required');
      if (action === 'activate') {
        await subscriptionPlanApi.activate(id);
      } else if (action === 'deactivate') {
        await subscriptionPlanApi.deactivate(id);
      } else {
        await subscriptionPlanApi.archive(id);
      }
    },
    entityName: 'subscriptionPlan',
    operation: 'update',
    onSuccess: () => {
      refetch();
    },
  });

  // Clone mutation
  const cloneMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Subscription Plan ID is required');
      return await subscriptionPlanApi.clone(id);
    },
    entityName: 'subscriptionPlan',
    operation: 'create',
    onSuccess: (cloned) => {
      navigate(`/admin/subscription-plans/${cloned.id}`);
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this plan?')) return;
    await deleteMutation.mutate();
  };

  const handleStatusChange = async (action: 'activate' | 'deactivate' | 'archive') => {
    if (!id) return;
    if (action === 'archive' && !window.confirm('Are you sure you want to archive this plan?')) {
      return;
    }
    await statusChangeMutation.mutate(action);
  };

  const handleClone = async () => {
    if (!id) return;
    await cloneMutation.mutate();
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return <LoadingState message="Loading subscription plan..." />;
  }

  if (error || !plan) {
    return <ErrorState error={error || new Error('Subscription plan not found')} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/subscription-plans">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {plan.displayName}
            </h2>
            <p className="text-muted-foreground mt-1">Plan Code: {plan.planCode}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/subscription-plans/${plan.id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" onClick={handleClone} disabled={cloneMutation.isLoading}>
              <Copy className="mr-2 h-4 w-4" />
              Clone
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Status Quick Change */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label>Status:</Label>
              <Select
                value={plan.status}
                onValueChange={(value) => {
                  if (value === 'ACTIVE') handleStatusChange('activate');
                  else if (value === 'INACTIVE') handleStatusChange('deactivate');
                  else if (value === 'ARCHIVED') handleStatusChange('archive');
                }}
                disabled={!canActivate || statusChangeMutation.isLoading}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              {plan.isPopular && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  Popular
                </span>
              )}
              {plan.isVisible ? (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Visible
                </span>
              ) : (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                  Hidden
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="geographical">Geographical</TabsTrigger>
          <TabsTrigger value="marketing">Marketing</TabsTrigger>
          <TabsTrigger value="quotas">Quotas</TabsTrigger>
          <TabsTrigger value="features">Features & Limits</TabsTrigger>
          <TabsTrigger value="trial">Trial Settings</TabsTrigger>
          <TabsTrigger value="availability">Availability</TabsTrigger>
          <TabsTrigger value="affiliators">Affiliator Associations</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Plan identification and classification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <CopyableId id={plan.planCode} label="Plan Code" />
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <p className="mt-1">{plan.displayName}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <textarea
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  value={plan.description}
                  disabled
                />
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label>Plan Type</Label>
                  <Input value={plan.planType} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Input value={plan.billingCycle} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Input value={plan.currency} disabled className="font-mono" />
                </div>
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input value={plan.displayOrder || 'N/A'} disabled />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Tab */}
        <TabsContent value="pricing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pricing Information</CardTitle>
              <CardDescription>Plan pricing details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Monthly Price</Label>
                  <Input
                    value={formatPrice(plan.monthlyPrice, plan.currency)}
                    disabled
                    className="text-lg font-semibold"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Yearly Price</Label>
                  <Input
                    value={formatPrice(plan.yearlyPrice, plan.currency)}
                    disabled
                    className="text-lg font-semibold"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Invoice Due Days</Label>
                <Input value={plan.invoiceDueDays || 7} disabled />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Geographical Tab */}
        <TabsContent value="geographical" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Geographical Support</CardTitle>
              <CardDescription>Supported countries, currencies, and payment gateways</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Supported Countries</Label>
                {plan.supportedCountries && plan.supportedCountries.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {plan.supportedCountries.map((country) => (
                      <span
                        key={country}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm font-mono"
                      >
                        {country}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No countries specified</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Supported Currencies</Label>
                {plan.supportedCurrencies && plan.supportedCurrencies.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {plan.supportedCurrencies.map((currency) => (
                      <span
                        key={currency}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm font-mono"
                      >
                        {currency}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No currencies specified</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Supported Payment Gateways</Label>
                {plan.supportedPaymentGateways && plan.supportedPaymentGateways.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {plan.supportedPaymentGateways.map((gateway) => (
                      <span
                        key={gateway}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm"
                      >
                        {gateway.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No payment gateways specified</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Marketing Tab */}
        <TabsContent value="marketing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Display & Marketing</CardTitle>
              <CardDescription>Marketing copy and display configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input value={plan.tagline || 'N/A'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>CTA Text</Label>
                  <Input value={plan.ctaText || 'N/A'} disabled />
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label>Badge Text</Label>
                  <Input value={plan.badgeText || 'N/A'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Icon Name</Label>
                  <Input value={plan.iconName || 'N/A'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Savings %</Label>
                  <Input value={plan.savingsPercentage != null ? `${plan.savingsPercentage}%` : 'N/A'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Recommended For</Label>
                  <Input value={plan.recommendedFor || 'N/A'} disabled />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Feature Highlights</Label>
                {plan.featureHighlights && plan.featureHighlights.length > 0 ? (
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {plan.featureHighlights.map((highlight, idx) => (
                      <li key={idx}>{highlight}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">No feature highlights</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Feature Flags</Label>
                {plan.featureFlags && Object.keys(plan.featureFlags).length > 0 ? (
                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(plan.featureFlags).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between px-3 py-2 rounded-md bg-secondary">
                        <span className="text-sm font-mono">{key}</span>
                        {value ? (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">ON</span>
                        ) : (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">OFF</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No feature flags configured</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quotas Tab */}
        <TabsContent value="quotas" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Limits & Quotas</CardTitle>
              <CardDescription>Token and session limits</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Daily Token Limit</Label>
                  <Input value={plan.dailyTokenLimit != null ? (plan.dailyTokenLimit === -1 ? 'Unlimited' : String(plan.dailyTokenLimit)) : 'N/A'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Token Limit</Label>
                  <Input value={plan.monthlyTokenLimit != null ? (plan.monthlyTokenLimit === -1 ? 'Unlimited' : String(plan.monthlyTokenLimit)) : 'N/A'} disabled />
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Max Coach Sessions Per Day</Label>
                  <Input value={plan.maxCoachSessionsPerDay != null ? (plan.maxCoachSessionsPerDay === -1 ? 'Unlimited' : String(plan.maxCoachSessionsPerDay)) : 'N/A'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Max Learning Paths</Label>
                  <Input value={plan.maxLearningPaths != null ? (plan.maxLearningPaths === -1 ? 'Unlimited' : String(plan.maxLearningPaths)) : 'N/A'} disabled />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features & Limits Tab */}
        <TabsContent value="features" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Features</CardTitle>
              <CardDescription>Plan features configuration</CardDescription>
            </CardHeader>
            <CardContent>
              {plan.features && Object.keys(plan.features).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(plan.features).map(([key, value]) => (
                    <div key={key} className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Feature</Label>
                        <Input value={key} disabled className="font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Value</Label>
                        <Input value={String(value)} disabled />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No features configured</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Limits</CardTitle>
              <CardDescription>Plan limits configuration</CardDescription>
            </CardHeader>
            <CardContent>
              {plan.limits && Object.keys(plan.limits).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(plan.limits).map(([key, value]) => (
                    <div key={key} className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Limit</Label>
                        <Input value={key} disabled className="font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Value</Label>
                        <Input value={String(value)} disabled />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No limits configured</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trial Settings Tab */}
        <TabsContent value="trial" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Trial Settings</CardTitle>
              <CardDescription>Trial configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Label>Trial Enabled:</Label>
                {plan.trialEnabled ? (
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>Yes</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-400">
                    <XCircle className="h-5 w-5" />
                    <span>No</span>
                  </div>
                )}
              </div>
              {plan.trialEnabled && (
                <div className="space-y-2">
                  <Label>Trial Duration (Days)</Label>
                  <Input value={plan.trialDurationDays || 'N/A'} disabled />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Availability Tab */}
        <TabsContent value="availability" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Availability</CardTitle>
              <CardDescription>Plan availability dates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Available From</Label>
                  <Input value={formatDate(plan.availableFrom)} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Available Until</Label>
                  <Input value={formatDate(plan.availableUntil)} disabled />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Affiliator Associations Tab */}
        <TabsContent value="affiliators" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Affiliator Associations</CardTitle>
              <CardDescription>Associated affiliator groups</CardDescription>
            </CardHeader>
            <CardContent>
              {plan.affiliatorGroupIds && plan.affiliatorGroupIds.length > 0 ? (
                <div className="space-y-2">
                  {plan.affiliatorGroupIds.map((groupId) => (
                    <div key={groupId} className="flex items-center gap-2">
                      <span className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm font-mono">
                        {groupId}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No affiliator associations</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Audit Information</CardTitle>
              <CardDescription>Plan creation and modification history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Created: </span>
                  <span>{formatDate(plan.createdAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated: </span>
                  <span>{formatDate(plan.updatedAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Version: </span>
                  <span>{plan.version || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Created By: </span>
                  <span>{plan.createdBy || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated By: </span>
                  <span>{plan.updatedBy || 'N/A'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SubscriptionPlanDetailPage;
